import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, AlertTriangle, XCircle, TrendingUp } from 'lucide-react';
import { KPIService } from '../../services/kpiService';

/**
 * Data Prep KPI Dashboard
 *
 * KPI #1: Discrepancy Check (Pass/Fail)
 * - Timer starts: project_setup_complete = true in Startup
 * - Timer ends: "Create Discrepancy Markup" subitem Done/N/A
 * - Timer pauses: On Hold or Paused
 * - Target: ≤ 3 business days
 * - Fail reasons: exceeded_target, retroactive_discrepancy
 *
 * KPI #2: Throughput (Avg Days in Data Prep)
 * - Target: ~4 business days
 */
const DataPrepDashboard = ({ startDate, endDate, periodLabel }) => {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const kpiData = await KPIService.getDataPrepKPIs(startDate, endDate);
        setKpis(kpiData);
      } catch (err) {
        console.error('Error fetching data prep KPIs:', err);
        setError('Failed to load KPI data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  if (loading) {
    return <div className="kpi-dashboard-loading">Loading...</div>;
  }

  if (error) {
    return <div className="kpi-dashboard-error">{error}</div>;
  }

  // Calculate fail reason breakdown
  const getFailReasonBreakdown = () => {
    if (!kpis?.timers) return { exceeded: 0, retroactive: 0 };

    const failedTimers = kpis.timers.filter(t => t.status === 'failed');
    const exceeded = failedTimers.filter(t => t.failReason === 'exceeded_target').length;
    const retroactive = failedTimers.filter(t => t.failReason === 'retroactive_discrepancy').length;

    return { exceeded, retroactive };
  };

  const failBreakdown = getFailReasonBreakdown();
  const failedCount = kpis?.outsideTarget || 0;
  const totalCount = kpis?.totalCompleted || 0;

  return (
    <div className="kpi-dashboard">
      {/* KPI Cards */}
      <div className="kpi-cards-row">
        {/* Discrepancy Check - Pass/Fail (Primary KPI) */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">Discrepancy Check</span>
            <div className={`kpi-card-icon ${failedCount === 0 ? 'green' : 'orange'}`}>
              {failedCount === 0 ? <CheckCircle size={18} /> : <XCircle size={18} />}
            </div>
          </div>
          <div className={`kpi-card-value ${failedCount === 0 ? 'success' : 'danger'}`}>
            {failedCount} / {totalCount}
          </div>
          <div className="kpi-card-target">
            jobs failed
          </div>
        </div>

        {/* Average Days (Throughput) */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">Avg Throughput</span>
            <div className="kpi-card-icon purple">
              <TrendingUp size={18} />
            </div>
          </div>
          <div className={`kpi-card-value ${kpis?.avgBusinessDays <= 4 ? 'success' : 'warning'}`}>
            {kpis?.avgBusinessDays?.toFixed(1) || '0.0'}
          </div>
          <div className="kpi-card-target">
            avg days (target: ~4)
          </div>
        </div>

        {/* Projects Processed */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-card-label">Projects Processed</span>
            <div className="kpi-card-icon blue">
              <Clock size={18} />
            </div>
          </div>
          <div className="kpi-card-value">
            {totalCount}
          </div>
          <div className="kpi-card-target">
            this period
          </div>
        </div>
      </div>

      {/* Fail Reason Breakdown */}
      {failedCount > 0 && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">Fail Reason Breakdown</h2>
          </div>
          <div className="kpi-section-content">
            <div className="kpi-fail-breakdown">
              <div className="kpi-fail-reason">
                <div className="kpi-fail-reason-icon exceeded">
                  <Clock size={16} />
                </div>
                <div className="kpi-fail-reason-content">
                  <span className="kpi-fail-reason-label">Exceeded Target</span>
                  <span className="kpi-fail-reason-desc">Took &gt; 3 business days</span>
                </div>
                <span className="kpi-fail-reason-count">{failBreakdown.exceeded}</span>
              </div>
              <div className="kpi-fail-reason">
                <div className="kpi-fail-reason-icon retroactive">
                  <AlertTriangle size={16} />
                </div>
                <div className="kpi-fail-reason-content">
                  <span className="kpi-fail-reason-label">Retroactive Discrepancy</span>
                  <span className="kpi-fail-reason-desc">Discrepancy created after completion</span>
                </div>
                <span className="kpi-fail-reason-count">{failBreakdown.retroactive}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Failed Projects List */}
      {kpis?.timers?.filter(t => t.status === 'failed').length > 0 && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">Failed Projects</h2>
          </div>
          <div className="kpi-section-content">
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Project Name</th>
                  <th>Net Days</th>
                  <th>Fail Reason</th>
                </tr>
              </thead>
              <tbody>
                {kpis.timers
                  .filter(t => t.status === 'failed')
                  .map((timer) => (
                    <tr key={timer.projectId}>
                      <td>{timer.jobNumber}</td>
                      <td>{timer.projectName}</td>
                      <td>{timer.businessDaysElapsed}</td>
                      <td>
                        <span className={`kpi-badge ${timer.failReason === 'exceeded_target' ? 'warning' : 'danger'}`}>
                          {timer.failReason === 'exceeded_target' ? 'Exceeded Target' : 'Retroactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Projects List */}
      {kpis?.timers?.length > 0 && (
        <div className="kpi-section">
          <div className="kpi-section-header">
            <h2 className="kpi-section-title">All Projects This Period</h2>
          </div>
          <div className="kpi-section-content">
            <table className="kpi-table">
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Project Name</th>
                  <th>Net Days</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {kpis.timers.slice(0, 15).map((timer) => (
                  <tr key={timer.projectId}>
                    <td>{timer.jobNumber}</td>
                    <td>{timer.projectName}</td>
                    <td>{timer.businessDaysElapsed}</td>
                    <td>
                      <span className={`kpi-badge ${timer.status === 'passed' ? 'success' : 'danger'}`}>
                        {timer.status === 'passed' ? 'Passed' : 'Failed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!kpis?.timers || kpis.timers.length === 0) && (
        <div className="kpi-section">
          <div className="kpi-empty">
            <Clock className="kpi-empty-icon" />
            <p className="kpi-empty-text">
              No projects processed during this period.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataPrepDashboard;
